import { forwardRef, memo } from 'react';
import { styled } from '@mui/material/styles';
import { SxProps, Typography } from '@mui/material';
import linkifyHtml from 'linkify-html';
import { highlightSearchKeyword } from 'utility/helpers';
import { decode } from 'html-entities';

type PostDescriptionProps = {
  description: string;
  searchKeyword?: string;
  sx?: SxProps;
};

const RenderRichText = forwardRef<HTMLParagraphElement, PostDescriptionProps>(
  ({ description, searchKeyword = '', sx }, articleDescriptionRef) => {
    if (description) {
      return (
        <Description
          ref={articleDescriptionRef}
          dangerouslySetInnerHTML={{
            __html: linkifyHtml(
              decode(
                searchKeyword ? highlightSearchKeyword(description, searchKeyword) : description,
              ),
              { target: '_blank' },
            ),
          }}
          sx={{
            ...sx,
          }}
        />
      );
    }

    return null;
  },
);

export const Description = styled(Typography)(({ theme }) => ({
  position: 'relative',
  wordBreak: 'break-word',
  fontFamily: 'Lato, sans-serif',
  fontSize: 16,
  color: theme.palette.custom.black,
  '& p': {
    margin: '0 0 8px',
  },
  '& *': {
    fontFamily: 'Lato, sans-serif !important',
    maxWidth: '100% !important',
  },
  '&.short, &.list, &.draft, &.related-post': {
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    // maxHeight: 150, // It's causing issues in rendering in Safari browser. So, hiding it for now. BWA-924
    textOverflow: 'ellipsis',
  },
  '&.short *, &.list *, &.draft *, &.related-post *': {
    // maxHeight: 100,
  },
  '& .highlight': {
    backgroundColor: `${theme.palette.custom.paleGold} !important`,
  },
  '& a': {
    whiteSpace: 'pre-wrap',
    color: `${theme.palette.custom.link} !important`,
    '&:hover': {
      textDecoration: 'underline !important',
    },
  },
  '&.related-post': {
    lineHeight: '20px',
    paddingTop: 4,
    fontSize: 14,
  },
  '& span.mention, & span.hashtag': {
    backgroundColor: theme.palette.custom.aliceBlue,
    color: theme.palette.custom.link,
    borderRadius: 4,
    padding: 2,
  },
  '& span.mention': {
    marginRight: 4,
  },
  '& span.hashtag:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

export default memo(RenderRichText);
